import { EUser, ESnapshotExists, ENotice, EOrganization } from 'lib/types';
import { getFirebaseContext } from 'utils/firebase';
import { removeUndefinedFields } from 'lib/helpers';
import { uuidv4 } from 'lib/utils/uuid';
import { EventInvoiceData, LineItem } from 'lib/types/invoices';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { safeAsync } from 'lib/safeWrappers';
import { UserNoticeModel } from 'lib/model/objects/userNoticeModel';
import { getModelFromSnapshot } from 'lib/model';
import { safeUpdateModel } from 'lib/model/safeHandlers';
import api from 'api';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import {
  getOrCreateAdvertiserStripeId,
  NOTICE_HAS_INVOICE_ERROR
} from 'services/billing';

type InvoiceAdvertiserProps = {
  lineItems: LineItem[];
  inAppTaxPct: number;
  noticeSnap: ESnapshotExists<ENotice>;
  advertiserSnap: ESnapshotExists<EUser>;
  newspaperSnap: ESnapshotExists<EOrganization>;
  customId?: string;
  purchaseOrderNumber?: string;
  customMemo?: string;
  dueDate?: number;
  requireUpfrontPayment?: boolean;
  invoiceOutsideColumn?: boolean;
  idempotencyKey?: string;
  user: ESnapshotExists<EUser> | undefined | null;
};

/**
 * Creates an event to trigger the creation of an invoice for a notice
 * We use FE data here because we allow invoice pricing to be manipulated in the
 * front-end form
 */
export const createNoticeInvoice = async ({
  lineItems,
  inAppTaxPct,
  noticeSnap,
  advertiserSnap,
  newspaperSnap,
  customId,
  purchaseOrderNumber,
  customMemo,
  dueDate,
  requireUpfrontPayment,
  invoiceOutsideColumn,
  idempotencyKey
}: InvoiceAdvertiserProps): Promise<ResponseOrError<void>> => {
  const ctx = getFirebaseContext();
  const noticeModel = getModelFromSnapshot(UserNoticeModel, ctx, noticeSnap);
  const [stripeIdError, stripeId] = await safeAsync(() =>
    getOrCreateAdvertiserStripeId(noticeModel, advertiserSnap)
  )();
  if (stripeIdError) {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      stripeIdError,
      'Unable to get or create advertiser stripe id',
      {
        noticeId: noticeModel.id,
        advertiserId: advertiserSnap.id
      }
    );
    return wrapError(stripeIdError);
  }

  const [noticeUpdateError] = await safeUpdateModel(noticeModel, {
    requireUpfrontPayment: !!requireUpfrontPayment
  });
  if (noticeUpdateError) {
    return wrapError(noticeUpdateError);
  }

  const [refreshNoticeError] = await safeAsync(() =>
    noticeModel.refreshData()
  )();
  if (refreshNoticeError) {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      refreshNoticeError,
      'Unable to refresh notice data',
      { noticeId: noticeModel.id }
    );
    return wrapError(refreshNoticeError);
  }

  if (noticeModel.modelData.invoice) {
    return wrapError(new Error(NOTICE_HAS_INVOICE_ERROR));
  }

  const invoiceData: Omit<EventInvoiceData, 'createdBy'> = {
    customer: stripeId,
    lineItems,
    inAppTaxPct,
    userNoticeId: noticeSnap.id,
    organizationId: newspaperSnap.id,
    advertiserId: advertiserSnap.id,
    customId,
    purchaseOrderNumber,
    customMemo,
    publisherId: customId || '', // NOTE:(kevinm) I believe this should be the newspaperSnap.id, not the customId, but we set the same value elsewhere so leaving it for now
    dueDate,
    invoiceOutsideColumn,
    idempotencyKey: idempotencyKey ?? uuidv4()
  };

  removeUndefinedFields(invoiceData);
  const { error: addEventError } = await api.safePost(
    'notices/create-invoice',
    invoiceData
  );
  if (addEventError) {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      new Error(addEventError),
      'Unable to create invoice creation stripe operation event',
      { noticeId: noticeModel.id }
    );
    return wrapError(new Error(addEventError));
  }

  return wrapSuccess(undefined);
};
